/**
 * The recall lifecycle state machine — ranks, ownership, and the rules that
 * decide whether a write may proceed.
 *
 * Deliberately pure: no Supabase client, no environment, no I/O. That is what
 * lets the rules below be unit-tested directly rather than through a mocked
 * query builder, and it is why this lives beside recallLifecycle.ts instead of
 * inside it.
 *
 * Two invariants are enforced here and nowhere else on the client:
 *
 *   1. State advances only. A recall that reached `completed` or `cancelled`
 *      never moves back to `selected` or `assigned`. This is P1 — finished work
 *      must never reappear as available work.
 *
 *   2. Each state has an owner. Selection and assignment are dispatcher actions
 *      and belong to the web; `in_progress` is driven by phone telemetry and
 *      belongs to the app. See WEB_WRITABLE_STATES.
 */

/**
 * Every state the column may hold, including values being retired.
 *
 * `viewed`, `printed` and `confirmed` are DEPRECATED — see DEPRECATED_STATES.
 * They remain in the union because production holds 25,447 rows carrying them
 * and an un-upgraded client may still send them; removing them from the type
 * would not remove them from the database.
 *
 * `unknown` is written by the backend's state reconstruction for rows whose
 * true stage cannot be recovered from surviving evidence. Preserving "we do not
 * know" is itself integrity — a guessed `assigned` would be worse.
 */
import { isFinalAssignmentStatus } from './assignmentStatus';

export type LifecycleState =
  | 'unknown'
  | 'viewed'
  | 'printed'
  | 'confirmed'
  | 'selected'
  | 'assigned'
  | 're_scheduled'
  | 're_assigned'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

/**
 * Ordering of the state machine. A write may only advance to a strictly higher
 * rank; equal or lower is refused.
 *
 * The four ranks that matter are the target model:
 *
 *     selected 30 → assigned 40 → in_progress 60 → completed/cancelled 90
 *
 * The rest exist so that a legacy value arriving from an un-upgraded client is
 * handled deterministically during the deprecation window:
 *
 *   - `unknown` (5) sits at the bottom so a reconstructed row is never frozen —
 *     any real stage may still be written over it.
 *   - `viewed` (10) and `printed` (20) sit below every real stage because a
 *     panel mounting is not a workflow stage. This is what makes the 21,295-row
 *     clobbering defect structurally impossible: `viewed` cannot mask anything.
 *   - `confirmed` (25) was never once written in eight months of production and
 *     is a property of the route, not the recall.
 *   - `re_scheduled` and `re_assigned` are PEERS at 50 — both mean "assigned,
 *     plan revised", so neither may silently overwrite the other. The durable
 *     record of a revision is its counter column, not this state.
 *   - `completed` and `cancelled` are terminal peers at 90, so neither can
 *     become the other.
 *
 * `satisfies` makes a missing state a compile error rather than a silent
 * `undefined` that would slip past every comparison below.
 */
export const LIFECYCLE_STATE_RANK = {
  unknown: 5,
  viewed: 10,
  printed: 20,
  confirmed: 25,
  selected: 30,
  assigned: 40,
  re_scheduled: 50,
  re_assigned: 50,
  in_progress: 60,
  completed: 90,
  cancelled: 90
} as const satisfies Record<LifecycleState, number>;

export const ALL_LIFECYCLE_STATES = Object.keys(LIFECYCLE_STATE_RANK) as LifecycleState[];

/**
 * States that end a recall permanently. Nothing may move a row out of one of
 * these, and no lower-ranked state may be written over one.
 */
export const TERMINAL_STATES = ['completed', 'cancelled'] as const;

/**
 * States no longer emitted by new writes. Each is an EVENT, not a stage, and
 * each already has its own timestamp column that is already being written —
 * `viewed_at`, `printed_at`, `confirmed_at`. Nothing is lost by retiring them
 * (P2): the information moves from a column that holds one value at a time to
 * columns that hold all of them.
 *
 * Reads must keep accepting these until the backend reconstruction runs and
 * telemetry shows no client still sends them.
 */
export const DEPRECATED_STATES = ['viewed', 'printed', 'confirmed'] as const;

/**
 * States this client is permitted to write.
 *
 * The web owns dispatcher actions: putting a recall in the pool, assigning it,
 * handing it to a different technician, moving it to a different date, and
 * recording the outcome.
 *
 * It must NEVER write `in_progress` — that means "a technician has started this
 * recall" and is driven by phone data, so only the app can honestly claim it.
 * A web-written `in_progress` would look like field telemetry that never
 * happened, and anything training on that signal would be reading an invention.
 *
 * Absence of `in_progress` therefore means "no phone telemetry", never "work
 * was not started": a recall completed entirely through the web goes
 * `assigned → completed`, skipping it, and the rank ordering permits that.
 */
export const WEB_WRITABLE_STATES = [
  'selected',
  'assigned',
  're_scheduled',
  're_assigned',
  'completed',
  'cancelled'
] as const;

export type TerminalState = (typeof TERMINAL_STATES)[number];
export type WebWritableState = (typeof WEB_WRITABLE_STATES)[number];

export function isLifecycleState(value: unknown): value is LifecycleState {
  return typeof value === 'string' && value in LIFECYCLE_STATE_RANK;
}

/**
 * Is this recall finished? Unrecognised and missing values answer `false`.
 *
 * The asymmetry is deliberate and runs the opposite way to isAdvanceAllowed
 * below. Here a wrong `true` would HIDE live work a dealer is waiting on, which
 * is the more damaging error; there a wrong `true` would let finished work be
 * overwritten. Each defaults away from its own worst case.
 */
export function isTerminalState(state: string | null | undefined): boolean {
  return state != null && (TERMINAL_STATES as readonly string[]).includes(state);
}

export function isDeprecatedState(state: string | null | undefined): boolean {
  return state != null && (DEPRECATED_STATES as readonly string[]).includes(state);
}

export function isWebWritableState(state: string | null | undefined): state is WebWritableState {
  return state != null && (WEB_WRITABLE_STATES as readonly string[]).includes(state);
}

/**
 * Every state a guarded write to `target` is allowed to advance FROM.
 *
 * This is the value handed to PostgREST as `.in('lifecycle_state', …)`, so the
 * guard is evaluated by Postgres inside the UPDATE rather than by us beforehand
 * — there is no window between deciding and writing.
 *
 * Two consequences worth stating plainly, because both are load-bearing:
 *
 *   - `.in()` matches neither NULL nor a value absent from the list, so the
 *     policy is "advance only from a known, strictly-lower state". A row whose
 *     state is NULL or unrecognised is not advanced; the caller falls through
 *     to the create path, and failing that records event timestamps only.
 *
 *   - No target's lower-set ever contains `completed` or `cancelled`, since
 *     they hold the maximum rank. That single property is what makes finished
 *     work unreachable by a state write, and it is pinned by a test.
 */
export function getLowerRankedStates(target: LifecycleState): LifecycleState[] {
  const targetRank = LIFECYCLE_STATE_RANK[target];
  return ALL_LIFECYCLE_STATES.filter(state => LIFECYCLE_STATE_RANK[state] < targetRank);
}

/**
 * Would a write of `target` over `current` be an advance?
 *
 * Used for instant client-side refusals against already-loaded state, so a user
 * is told no in the same frame rather than after a round trip. The database
 * guard is the authority; this is the fast path, and it must agree with it.
 *
 * An unrecognised or missing `current` answers `false` — fail closed. The
 * alternative would let an unfamiliar value re-open finished work.
 */
export function isAdvanceAllowed(
  current: string | null | undefined,
  target: LifecycleState
): boolean {
  if (!isLifecycleState(current)) return false;
  return LIFECYCLE_STATE_RANK[current] < LIFECYCLE_STATE_RANK[target];
}

/**
 * Remove keys whose value is `undefined`.
 *
 * This is what makes a lifecycle write non-clobbering, and the distinction is
 * not cosmetic: PostgREST builds `ON CONFLICT DO UPDATE SET` from the keys
 * present in the request body. A key that is absent leaves the column alone; a
 * key set to `null` erases it. Both behaviours are needed — omission for the
 * ordinary case, explicit `null` for the recycle reset that genuinely intends
 * to clear a field.
 *
 * So `undefined` must never reach the wire, or it becomes an accidental erase.
 */
export function stripUndefined<T extends Record<string, unknown>>(record: T): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result as Partial<T>;
}

/**
 * Columns that must never appear in a lifecycle write body.
 *
 * `id` and `created_at` are owned by the database. Sending `id` on an upsert
 * makes the conflict resolution ambiguous; sending `created_at` rewrites when
 * the row was first seen, which is the one timestamp that should never move.
 */
export const NEVER_WRITE_COLUMNS = ['id', 'created_at'] as const;

/**
 * Fields still safe to record when a state advance has been REFUSED.
 *
 * A refusal means the row is at an equal or higher rank — most importantly, it
 * may be `completed`. So the refusal path must write nothing that could damage
 * a completion record: not `technician_id`, not `final_status`, not
 * `assignment_status`.
 *
 * Event timestamps are the exception. Someone genuinely did view or print this
 * recall, and recording that is harmless and useful. Only `lifecycle_state` is
 * monotonic; the event columns are append-in-place facts.
 *
 * This inverts today's pathology, where 5,762 rows sit in `viewed` while
 * carrying a `printed_at` — the state overwrote the stage, and the event that
 * should have been recorded alongside it was the only survivor.
 */
export const REFUSED_ADVANCE_SAFE_FIELDS = [
  'viewed_at',
  'printed_at',
  'printed_by_user_id',
  'confirmed_at',
  'updated_at'
] as const;

/**
 * Reduce a payload to the subset that may be written when the advance was
 * refused. Returns an empty object when nothing survives, which the caller
 * should read as "no write needed" rather than "write nothing to everything".
 */
export function pickRefusedAdvanceFields<T extends Record<string, unknown>>(
  payload: T
): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const field of REFUSED_ADVANCE_SAFE_FIELDS) {
    const value = payload[field];
    if (value !== undefined) {
      result[field] = value;
    }
  }
  return result as Partial<T>;
}

/**
 * Build the body of a lifecycle write.
 *
 * Strips `undefined`, drops the database-owned columns, and guarantees the
 * conflict-target columns are present — an upsert without them cannot resolve
 * its conflict target and would insert a duplicate rather than update.
 */
export function buildLifecyclePayload(
  identity: { vin: string; campaign_number: string; tenant_id: string },
  updates: Record<string, unknown>
): Record<string, unknown> {
  const cleaned = stripUndefined(updates);
  for (const column of NEVER_WRITE_COLUMNS) {
    delete cleaned[column];
  }
  return {
    ...cleaned,
    vin: identity.vin,
    campaign_number: identity.campaign_number,
    tenant_id: identity.tenant_id
  };
}

/**
 * Split rows into groups that share an identical key set.
 *
 * PostgREST builds one `ON CONFLICT DO UPDATE SET` clause per request from the
 * keys of the FIRST object, and requires every object in the batch to carry the
 * same keys. Padding the short ones with `null` to satisfy that would erase the
 * columns they omitted — which is the whole hazard this module exists to
 * prevent. Partitioning by key set is the alternative: a few more requests,
 * none of them destructive.
 */
export function partitionUpsertRows<T extends Record<string, unknown>>(rows: T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const signature = Object.keys(row).sort().join('|');
    const group = groups.get(signature);
    if (group) {
      group.push(row);
    } else {
      groups.set(signature, [row]);
    }
  }
  return [...groups.values()];
}

/** What a caller knows about the assignment behind a recall. */
export interface ReconciliationEvidence {
  assignmentId?: string | null | undefined;
  assignmentStatus?: string | null | undefined;
  completedAt?: string | null | undefined;
}

/**
 * One conditional repair. It applies only to a lifecycle row whose
 * `final_status` is exactly `whenFinalStatus` (null meaning "none recorded"),
 * and it writes only `fields`. The caller adds the state itself.
 */
export interface ReconciliationArm {
  whenFinalStatus: string | null;
  fields: Record<string, string>;
}

export interface ReconciliationPlan {
  target: Extract<LifecycleState, 'completed' | 'cancelled'>;
  /**
   * Mutually exclusive — a row has exactly one final_status — so a caller may
   * issue them together, and at most one can match.
   */
  arms: ReconciliationArm[];
}

/**
 * Decide how a lifecycle record may be corrected from its assignment. Returns
 * null when there is nothing safe to do.
 *
 * Pure, so the rules that decide whether production rows get rewritten are
 * tested directly. The caller performs the writes, each a guarded advance.
 *
 * ── The rule: act only where the two records AGREE ──
 *
 * The target comes from the assignment row, but every write also requires the
 * lifecycle record's own `final_status` to agree, enforced inside the UPDATE
 * so nothing can change between checking and writing. Where the records
 * disagree the repair does nothing and leaves the row for the human-reviewed
 * backend repair (B12). Measured 2026-09-11: 4,822 rows agree, 548 do not.
 *
 *   completed, when final_status is 'completed'
 *       RLM-04: the outcome survived and only the state was clobbered.
 *       Restore the state; leave the completion record exactly as it is (P3).
 *   completed, when final_status is NULL
 *       RLM-63: the completion never arrived. Record it from the assignment.
 *   completed is NEVER applied over 'vehicle_sold' — that would overwrite a
 *       recorded outcome with a different one (42 rows).
 *
 *   cancelled, when final_status is 'vehicle_sold'
 *       Both records say the recall ended: the car left inventory.
 *   cancelled is NEVER applied over:
 *       'other'      486 rows. Mostly genuine close-outs, but 7 carry a
 *                    recycle-class reason — recalls a technician sent back to
 *                    the pool that the Phase 2 bug wrongly cancelled. Closing
 *                    those hides live work, the worse failure, and free text is
 *                    not trustworthy enough to decide a write that hides work.
 *       NULL         we would know it ended, not why
 *       'not_there'  still an open decision (backend item B5)
 *       'completed'  contradicts the assignment
 *
 * Also never: a live assignment status (a recall being worked is never marked
 * finished), an unrecognised one, a servicing technician (we know the work
 * ended, not who finished it), or a null value (nothing is erased).
 */
export function planReconciliation(evidence: ReconciliationEvidence): ReconciliationPlan | null {
  const status = evidence.assignmentStatus;
  if (!status || !isFinalAssignmentStatus(status)) return null;

  const shared: Record<string, string> = {
    assignment_status: status,
    // Backfilling this is what lets the mobile app reach the row next time —
    // the root cause of RLM-63, rather than its symptom.
    ...(evidence.assignmentId ? { assignment_id: evidence.assignmentId } : {})
  };

  if (status === 'completed') {
    return {
      target: 'completed',
      arms: [
        { whenFinalStatus: 'completed', fields: shared },
        {
          whenFinalStatus: null,
          fields: {
            ...shared,
            final_status: 'completed',
            ...(evidence.completedAt ? { completed_at: evidence.completedAt } : {})
          }
        }
      ]
    };
  }

  return {
    target: 'cancelled',
    arms: [{ whenFinalStatus: 'vehicle_sold', fields: shared }]
  };
}

/**
 * Render a required `final_status` as a PostgREST filter expression for
 * `.or()`: null becomes `final_status.is.null`, 'completed' becomes
 * `final_status.eq.completed`.
 *
 * The values are fixed literals from planReconciliation, never user input, so
 * there is nothing to escape.
 */
export function finalStatusFilter(value: string | null): string {
  if (value === null) return 'final_status.is.null';
  return `final_status.eq.${value}`;
}

/**
 * Pick the repair branch that applies to a row as it stands, or null.
 *
 * Two conditions, both required:
 *   - the state must still be below the target, so a row that is already
 *     terminal is never rewritten;
 *   - the row's own `final_status` must match a branch, which is what "the two
 *     records agree" means in practice.
 *
 * Callers still send the same conditions in the UPDATE. Deciding here first only
 * avoids issuing a request that would match nothing — it is not the guard.
 */
export function chooseReconciliationArm(
  plan: ReconciliationPlan,
  current: { lifecycle_state?: string | null; final_status?: string | null }
): ReconciliationArm | null {
  if (!isAdvanceAllowed(current.lifecycle_state, plan.target)) return null;
  const wanted = current.final_status ?? null;
  return plan.arms.find(arm => arm.whenFinalStatus === wanted) ?? null;
}
